/**
 * LANDING CONTENT — Édite ce fichier pour changer le texte de la landing.
 *
 * REFONTE 2026-07 — le site public se limite aux DEUX offres :
 *   - Gestic Pro   (budget complet au centime + véhicule + patrimoine + vie commune)
 *   - Gestic Light (les grandes lignes, sans saisie fine)
 * Les prix affichés viennent de lib/stripe/plans.ts (source de vérité
 * partagée avec le checkout Stripe) — ici on ne met QUE la copie.
 *
 * Convention :
 *   - eyebrow : petit texte au-dessus du titre (souvent en majuscules)
 *   - title   : titre principal de la section
 *   - subtitle: paragraphe d'intro sous le titre
 *   - features: liste des points forts (titre + description courte)
 *   - cta     : texte du bouton d'action
 */

export const LANDING = {
  /* ──────────────────────────────────────
     NAV (haut de page, sticky)
     ────────────────────────────────────── */
  nav: {
    brand: "Gestic",
    links: [
      { label: "L'histoire", href: "#histoire" },
      { label: "Les modules", href: "#modules" },
      { label: "Tarifs", href: "#pricing" },
      { label: "Transparence", href: "#transparence" },
    ],
    ctaSecondary: { label: "Connexion", href: "/login" },
    ctaPrimary: { label: "Essai gratuit 14 j", href: "#pricing" },
  },

  /* ──────────────────────────────────────
     HERO (en haut, première impression)
     ────────────────────────────────────── */
  hero: {
    eyebrow: "Budget personnel · patrimoine · prévisionnel",
    title: "Ton argent, au centime près ou en grandes lignes.",
    titleAccent: "au centime près ou en grandes lignes.",
    subtitle:
      "Gestic est une app de budget française, sobre et rigoureuse : trésorerie prévisionnelle fiable au centime, épargne, patrimoine, dépenses partagées, véhicule. Deux formules selon ton niveau d'exigence — même moteur, même sérieux.",
    ctaPrimary: { label: "Commencer — 14 jours offerts", href: "#pricing" },
    ctaSecondary: { label: "Découvrir les formules", href: "#pro" },
    // Petits badges/argumentaires sous les CTA
    bullets: [
      "✓ Essai gratuit 14 jours",
      "✓ Sans engagement, résiliable en 2 clics",
      "✓ Données chiffrées, hébergées en Europe",
      "✓ 100 % web (PWA), aucune installation",
    ],
  },

  /* ──────────────────────────────────────
     GESTIC PRO — l'offre complète (#pro)
     ────────────────────────────────────── */
  budget: {
    eyebrow: "Gestic Pro",
    title: "Le budget complet, au centime près",
    subtitle:
      "Multi-comptes, charges fixes, enveloppes, trésorerie prévisionnelle chaînée, épargne & patrimoine, dépenses partagées en couple, suivi véhicule — chaque chiffre affiché se recoupe, jusqu'au centime.",
    features: [
      {
        icon: "🏦",
        title: "Multi-banques & caisse espèces",
        description:
          "Autant de comptes que tu veux, chacun sa couleur — et le liquide vit dans une vraie caisse : retraits, dépôts, dépenses cash.",
      },
      {
        icon: "📈",
        title: "Trésorerie prévisionnelle fiable",
        description:
          "Solde réalisé et solde prévisionnel chaînés mois par mois, budgets et épargne distingués, écart expliqué ligne à ligne. Zéro chiffre mystère.",
      },
      {
        icon: "📌",
        title: "Charges fixes & revenus récurrents",
        description:
          "Crée une série une fois, elle se propage chaque mois. Modification d'une occurrence ou de toute la série, jour de prélèvement, banque.",
      },
      {
        icon: "🎯",
        title: "Budgets-enveloppes intelligents",
        description:
          "Pose tes enveloppes par catégorie et par banque. Un budget non consommé sur un mois terminé disparaît — il ne pollue jamais tes projections.",
      },
      {
        icon: "🌱",
        title: "Épargne & patrimoine",
        description:
          "Livrets, PEE avec abondement, objectifs, projections fin d'année, patrimoine financier et corporel — le tout branché sur ta trésorerie.",
      },
      {
        icon: "👫",
        title: "Vie commune & dépenses partagées",
        description:
          "Répartition 60/40 (ou ce que tu veux), remboursements façon tricount, charges fixes partagées synchronisées entre les comptes du couple.",
      },
      {
        icon: "🚗",
        title: "Module Véhicule inclus",
        description:
          "Échéances (CT, assurance, entretien) qui alimentent tes budgets futurs, carnet d'entretien, suivi carburant et consommation.",
      },
      {
        icon: "📊",
        title: "Analytics fines",
        description:
          "Répartition des dépenses, zoom trésorerie jour par jour avec point bas du mois, comparaisons — les chiffres parlent.",
      },
      {
        icon: "📱",
        title: "PWA rapide, hors-ligne",
        description:
          "Installable sur iPhone, Android et Mac. Consultation hors-ligne, synchronisation temps réel entre tes appareils.",
      },
    ],
  },

  /* ──────────────────────────────────────
     GESTIC LIGHT (#light)
     ────────────────────────────────────── */
  modules: {
    eyebrow: "Gestic Light",
    title: "Les grandes lignes, sans saisie au centime",
    subtitle:
      "Tu ne veux pas pointer chaque ticket de caisse ? Light garde la même rigueur de calcul, mais en fourchettes : tes récurrents, tes soldes recalés de temps en temps, et une vraie vision patrimoine + prévisionnel.",
    items: [
      {
        id: "vue-ensemble",
        emoji: "🪶",
        badge: "Light",
        title: "Vue d'ensemble",
        tagline: "Ton patrimoine net en un écran",
        description:
          "Comptes, épargne, biens : le patrimoine net en héro, la marge mensuelle indicative, et tes revenus / charges / enveloppes en fourchettes — modifiables d'un clic.",
        accentColor: "#4C956C",
        features: ["Patrimoine net + allocation", "Récurrents en fourchettes", "Marge mensuelle indicative"],
      },
      {
        id: "recalage",
        emoji: "🎚️",
        badge: "Light",
        title: "Recalage de solde",
        tagline: "30 secondes par semaine suffisent",
        description:
          "Pas de pointage : tu déclares le solde réel affiché par ta banque, Gestic pose l'écart et repart juste. La rigueur sans la corvée.",
        accentColor: "#2D6A4F",
        features: ["Solde déclaré → écart auto", "Aucun pointage", "Historique conservé"],
      },
      {
        id: "previsionnel",
        emoji: "📈",
        badge: "Light",
        title: "Prévisionnel Pro",
        tagline: "Le même graphique que Gestic Pro",
        description:
          "Courbes réalisé + prévisionnel chaîné, budgets distingués de l'épargne, détail par mois cliquable, zoom jour par jour. Exactement le moteur de Pro, branché sur tes grandes lignes.",
        accentColor: "#456990",
        features: ["Courbe réalisé / prévis", "Détail par mois", "Zoom jour par jour"],
      },
      {
        id: "partage",
        emoji: "👥",
        badge: "Light",
        title: "Possessions & groupes d'amis",
        tagline: "Patrimoine et dépenses partagées",
        description:
          "Le même écran Possessions que Pro (épargne, objectifs, biens), et les groupes d'amis en option : dépenses partagées et remboursements façon tricount.",
        accentColor: "#B5838D",
        features: ["Possessions complet", "Tricount entre amis", "Activable en un toggle"],
      },
    ],
  },

  /* ──────────────────────────────────────
     PRICING — 2 formules (les prix viennent de lib/stripe/plans.ts)
     ────────────────────────────────────── */
  pricing: {
    eyebrow: "Tarifs honnêtes",
    title: "Deux formules, zéro piège",
    subtitle:
      "Essai gratuit 14 jours sur les deux formules, sans engagement, résiliation en 2 clics depuis l'app. Le paiement active ton accès instantanément — pas de liste d'attente, pas de démo bridée.",
    plans: [
      {
        id: "light",
        checkoutPlan: "light" as const,
        name: "Gestic Light",
        price: "2 €",
        priceNumber: "2.00",
        priceSuffix: "/mois",
        tagline: "Les grandes lignes, la vraie vision",
        cta: { label: "Essayer Light 14 jours", href: "#" },
        featured: false,
        trialNote: "14 jours offerts, puis 2 €/mois. Annulable pendant l'essai : 0 €.",
        features: [
          "Vue d'ensemble patrimoine net",
          "Revenus, charges & enveloppes en fourchettes",
          "Recalage de solde (zéro pointage)",
          "Prévisionnel : le graphique de Pro",
          "Possessions (épargne, objectifs, biens)",
          "Groupes d'amis / tricount (en option)",
        ],
      },
      {
        id: "pro",
        checkoutPlan: "pro" as const,
        name: "Gestic Pro",
        price: "3,99 €",
        priceNumber: "3.99",
        priceSuffix: "/mois",
        tagline: "Le budget complet, au centime près",
        cta: { label: "Essayer Pro 14 jours", href: "#" },
        featured: true,
        trialNote: "14 jours offerts, puis 3,99 €/mois. Annulable pendant l'essai : 0 €.",
        features: [
          "Tout Gestic Light, plus :",
          "Dépenses & revenus au centime, pointage",
          "Trésorerie prévisionnelle chaînée + analytics",
          "Charges fixes, budgets, emprunts",
          "Épargne, PEE, objectifs, patrimoine",
          "Vie commune : dépenses & fixes partagées",
          "Module Véhicule complet inclus",
        ],
      },
    ],
  },

  /* ──────────────────────────────────────
     TRANSPARENCE (#transparence) — où va ton argent
     ────────────────────────────────────── */
  transparence: {
    eyebrow: "Transparence totale",
    title: "Où va ton argent, exactement",
    subtitle:
      "Gestic est développé par une seule personne, en France, sans investisseurs, sans publicité et sans revente de données. Voici le vrai décompte de chaque abonnement — frais bancaires compris.",
    stripeExplainer: {
      title: "Les frais Stripe, expliqués",
      lines: [
        "Chaque paiement carte passe par Stripe (le standard du paiement en ligne, utilisé par Deezer ou Leboncoin).",
        "Stripe prélève 1,5 % + 0,25 € par transaction (carte européenne), plus 0,7 % pour la gestion des abonnements et de l'essai gratuit.",
        "Le forfait fixe de 0,25 € pèse mécaniquement plus lourd sur un petit prix : c'est pour ça qu'un abonnement à 2 € laisse ~15 % à Stripe, contre ~9 % à 3,99 €.",
      ],
    },
    pourquoi: {
      title: "Pourquoi ces tarifs",
      bullets: [
        {
          icon: "🧮",
          title: "Prix coûtant + un café",
          text: "Le net restant couvre l'hébergement (base de données européenne, serveurs, domaine, emails) et rémunère le développement. Pas de marge cachée : l'app est rentable dès quelques dizaines d'abonnés, pas des milliers.",
        },
        {
          icon: "🚫",
          title: "Pas de formule gratuite",
          text: "Chaque compte coûte réellement (stockage, calculs, sauvegardes). Un « gratuit » se paierait en pub ou en données revendues — c'est non. L'essai de 14 jours fait le même travail, honnêtement.",
        },
        {
          icon: "🔒",
          title: "Tes données ne sont pas le produit",
          text: "Aucune connexion bancaire automatique, aucun scraping, aucune revente. Tes chiffres restent les tiens, chiffrés, en Europe, supprimables sur simple demande.",
        },
        {
          icon: "✂️",
          title: "Résiliation en 2 clics",
          text: "Le portail Stripe est accessible depuis les réglages de l'app : tu résilies quand tu veux, effectif immédiatement, sans email de justification ni bouton caché.",
        },
      ],
    },
  },

  /* ──────────────────────────────────────
     FAQ
     ────────────────────────────────────── */
  faq: {
    eyebrow: "Questions fréquentes",
    title: "Tout ce qu'il faut savoir",
    items: [
      {
        q: "Comment fonctionne l'essai gratuit de 14 jours ?",
        a: "Tu choisis ta formule, tu renseignes ta carte chez Stripe (elle n'est pas débitée), et ton accès s'active immédiatement pour l'email utilisé au paiement. Si tu annules pendant l'essai — 2 clics depuis les réglages — tu ne paies rien du tout.",
      },
      {
        q: "Quelle différence entre Gestic Pro et Gestic Light ?",
        a: "Le même moteur de calcul, deux niveaux de détail. Pro : chaque dépense au centime, pointage bancaire, budgets, épargne, vie commune, véhicule. Light : tes récurrents en fourchettes et un recalage de solde de temps en temps, plus le suivi du patrimoine. Light se gère en 5 minutes par semaine, Pro en 5 minutes par jour.",
      },
      {
        q: "Le module Véhicule est-il vraiment inclus dans Pro ?",
        a: "Oui, sans supplément : échéances (contrôle technique, assurance, entretien) qui créent automatiquement des budgets sur les bons mois, carnet d'entretien avec presets constructeur, suivi carburant et consommation au kilomètre.",
      },
      {
        q: "Mes données sont-elles en sécurité ?",
        a: "Oui. Authentification à deux facteurs (passkey ou TOTP), base PostgreSQL européenne avec Row Level Security : chaque utilisateur ne peut physiquement lire que ses propres lignes. Pas de connexion à ta banque : tu saisis ou importes ce que tu veux, rien ne sort.",
      },
      {
        q: "Comment je résilie ?",
        a: "Depuis les réglages de l'app → « Gérer mon abonnement » : tu arrives sur le portail Stripe, tu annules, c'est fini. L'accès se coupe en fin de période payée (ou immédiatement en fin d'essai). Tes données peuvent être supprimées sur demande.",
      },
      {
        q: "Pourquoi une carte est demandée pour l'essai ?",
        a: "C'est le fonctionnement de l'essai Stripe : la carte est enregistrée mais débitée seulement au 15e jour si tu restes. Ça évite les comptes jetables et ça garde le prix bas pour tout le monde. Stripe te rappelle par email avant la fin de l'essai.",
      },
      {
        q: "L'app fonctionne-t-elle sur mobile ?",
        a: "Oui, c'est une PWA installable : sur iOS et Android, ajoute-la à ton écran d'accueil et utilise-la comme une app native, avec consultation hors-ligne. Une seule base de données, tous tes appareils synchronisés.",
      },
      {
        q: "Puis-je importer mes données existantes ?",
        a: "Oui : import CSV des transactions depuis n'importe quelle banque. Et si tu viens d'une autre app avec un format exotique, écris-moi — je regarde ce qui est faisable.",
      },
    ],
  },

  /* ──────────────────────────────────────
     CTA FINAL
     ────────────────────────────────────── */
  finalCta: {
    title: "14 jours pour voir clair dans ton argent.",
    subtitle:
      "Choisis ta formule, active ton accès en 2 minutes. Si ce n'est pas pour toi, tu annules pendant l'essai et ça ne t'aura rien coûté.",
    ctaPrimary: { label: "Choisir ma formule", href: "#pricing" },
  },

  /* ──────────────────────────────────────
     FOOTER
     ────────────────────────────────────── */
  footer: {
    brand: "Gestic",
    tagline: "L'app all-rounder de ta vie : budget, patrimoine, intermittence, voiture, famille. Fait main en France, sans publicité ni revente de données.",
    columns: [
      {
        title: "Produit",
        links: [
          { label: "L'histoire", href: "#histoire" },
          { label: "Les modules", href: "#modules" },
          { label: "Tarifs", href: "#pricing" },
          { label: "Transparence", href: "#transparence" },
        ],
      },
      {
        title: "Compte",
        links: [
          { label: "Connexion", href: "/login" },
          { label: "Essai gratuit", href: "#pricing" },
          // Résiliation en ligne facilement accessible (C. conso., art. L215-1-1).
          { label: "Résilier mon abonnement", href: "/api/stripe/resilier" },
        ],
      },
      {
        title: "Légal",
        links: [
          { label: "Mentions légales", href: "/legal/mentions" },
          { label: "Conditions générales", href: "/legal/cgv" },
          { label: "Confidentialité", href: "/legal/confidentialite" },
        ],
      },
    ],
    bottomLine: "© 2026 Gestic. Fait à la main, en France.",
  },
};

/**
 * SEO — Métadonnées de la page (titre onglet, description Google,
 * Open Graph pour les liens partagés).
 */
export const LANDING_SEO = {
  // Titre ≤ 60 caractères, mots cherchés en tête (« application de budget ») ;
  // la description (≤ 160) porte le récit et ce qui distingue Gestic.
  title: "Gestic — Application de budget, patrimoine et vie quotidienne",
  description:
    "Budget au centime ou en tendances, patrimoine, intermittence du spectacle, voiture, arbre généalogique : une seule app française et sans pub. 14 jours offerts.",
  keywords: [
    "application budget",
    "gestion budget personnel",
    "budget prévisionnel",
    "suivi patrimoine",
    "dépenses partagées couple",
    "intermittent du spectacle 507 heures",
    "suivi cachets intermittent",
    "arbre généalogique en ligne",
    "entretien voiture suivi",
  ],
  ogImage: "/og-image.png",
  twitterHandle: "",
  canonicalUrl: "https://gestic.fr",
};
